import { Injectable } from '@angular/core';
import { MarkdownService } from 'ngx-markdown';
import { BehaviorSubject, timer } from 'rxjs';
import { QuickBuild } from 'src/app/people/quick-build.service';

export interface DrawerConfig {
  mode?: 'filters' | 'data-selection';
  quickBuild?: QuickBuild | null;
  matchingRecords?: number | null;
  title?: string;
  customColumns?: string[] | null;
  suggestedFilename?: string | null;
}

const DEFAULT_DRAWER_CONFIG: Required<DrawerConfig> = {
  mode: 'filters',
  quickBuild: null,
  matchingRecords: null,
  title: 'Data to Download',
  customColumns: null,
  suggestedFilename: null,
};

function mergeWithDefaults(config: Partial<DrawerConfig>): Required<DrawerConfig> {
  return {
    ...DEFAULT_DRAWER_CONFIG,
    ...config,
  };
}

/**
 * Service to manage the state of the Streams Subscribe Drawer
 * This service is used to communicate between the drawer component and other components
 * that need to know about the drawer's state (like the main app component)
 */
@Injectable({
  providedIn: 'root',
})
export class StreamsSubscribeDrawerService {
  private isDrawerOpen = new BehaviorSubject<boolean>(false);
  private drawerConfig = new BehaviorSubject<Required<DrawerConfig>>(DEFAULT_DRAWER_CONFIG);

  // Observables that emit the current state of the drawer
  isDrawerOpen$ = this.isDrawerOpen.asObservable();
  drawerConfig$ = this.drawerConfig.asObservable();

  constructor(private markdownService: MarkdownService) {}

  // opens drawer & emits state
  openDrawer(config: Partial<DrawerConfig> = {}) {
    this.drawerConfig.next(mergeWithDefaults(config));
    this.isDrawerOpen.next(true);
  }

  // closes drawer & emits state
  closeDrawer() {
    this.isDrawerOpen.next(false);
  }

  // returns current state of drawer for use in app component
  isOpen(): boolean {
    return this.isDrawerOpen.value;
  }

  // Update specific drawer configuration
  updateConfig(config: Partial<DrawerConfig>) {
    const currentConfig = this.drawerConfig.value;
    this.drawerConfig.next(
      mergeWithDefaults({
        ...currentConfig,
        ...config,
      })
    );
  }
}
